import { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useAuth } from '@/store/auth';
import { useT } from '@/store/i18n';
import type { Role } from '@/types';

interface PaletteItem {
  id: string;
  label: string;
  hint?: string;
  group: string;
  to?: string;
  action?: () => void;
  keywords?: string;
  roles?: Role[];
}

interface Props {
  open: boolean;
  onClose: () => void;
}

export function CommandPalette({ open, onClose }: Props) {
  const t = useT();
  const navigate = useNavigate();
  const role = useAuth((s) => s.user?.role);
  const [query, setQuery] = useState('');
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setQuery('');
      setActiveIdx(0);
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const GOTO = t('palette.group.goto');
  const ACTIONS = t('palette.group.actions');

  const allItems: PaletteItem[] = [
    { id: 'nav-dashboard', label: t('palette.nav.dashboard'), group: GOTO, to: '/' },
    { id: 'nav-questions', label: t('palette.nav.questions'), group: GOTO, to: '/questions', roles: ['teacher', 'academic_admin', 'admin'] },
    { id: 'nav-approvals', label: t('palette.nav.approvals'), group: GOTO, to: '/approvals', roles: ['academic_admin', 'admin'] },
    { id: 'nav-bank', label: t('palette.nav.bank'), group: GOTO, to: '/question-bank' },
    { id: 'nav-indicators', label: t('palette.nav.indicators'), group: GOTO, to: '/learning-indicators', roles: ['teacher', 'academic_admin', 'admin'] },
    { id: 'nav-reports', label: t('palette.nav.reports'), group: GOTO, to: '/reports', roles: ['admin', 'academic_admin'] },
    { id: 'nav-users', label: t('palette.nav.users'), group: GOTO, to: '/users', roles: ['admin'] },
    { id: 'nav-perms', label: t('palette.nav.perms'), group: GOTO, to: '/subject-permissions', roles: ['admin'] },
    { id: 'nav-meta', label: t('palette.nav.metadata'), group: GOTO, to: '/metadata', roles: ['admin'] },
    { id: 'nav-backup', label: t('palette.nav.backup'), group: GOTO, to: '/backup', roles: ['admin'] },

    {
      id: 'act-create',
      label: t('palette.act.create'),
      group: ACTIONS,
      to: '/questions/new',
      roles: ['teacher', 'academic_admin', 'admin'],
    },
  ];

  const visible = allItems.filter((it) => {
    if (it.roles && (!role || !it.roles.includes(role))) return false;
    const q = query.trim().toLowerCase();
    if (!q) return true;
    return (
      it.label.toLowerCase().includes(q) ||
      it.keywords?.toLowerCase().includes(q)
    );
  });

  const groupOrder = [GOTO, ACTIONS];
  const grouped = groupOrder
    .map((g) => ({ name: g, items: visible.filter((i) => i.group === g) }))
    .filter((g) => g.items.length > 0);

  function runIndex(idx: number) {
    const item = visible[idx];
    if (!item) return;
    onClose();
    setTimeout(() => {
      if (item.to) navigate(item.to);
      else item.action?.();
    }, 50);
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(i + 1, visible.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      runIndex(activeIdx);
    }
  }

  if (!open) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[9990] flex items-start justify-center p-4 sm:p-6 bg-slate-900/40 backdrop-blur-sm animate-fade-in"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-slate-200 mt-[10vh] overflow-hidden animate-slide-down flex flex-col max-h-[70vh]">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-100">
          <Search className="w-4 h-4 text-slate-400" />
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setActiveIdx(0);
            }}
            onKeyDown={onKeyDown}
            placeholder={t('palette.placeholder')}
            className="flex-1 bg-transparent border-none outline-none text-sm text-slate-900 placeholder:text-slate-400"
          />
          <kbd className="text-[10px] font-mono px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded text-slate-500">
            ESC
          </kbd>
        </div>

        <div className="flex-1 overflow-y-auto py-2">
          {visible.length === 0 ? (
            <div className="py-10 text-center text-sm text-slate-400">
              {t('palette.empty')}
            </div>
          ) : (
            grouped.map((g) => (
              <div key={g.name} className="px-2 pb-1">
                <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-slate-400">
                  {g.name}
                </div>
                {g.items.map((it) => {
                  const idx = visible.findIndex((v) => v.id === it.id);
                  const isActive = idx === activeIdx;
                  return (
                    <button
                      type="button"
                      key={it.id}
                      onMouseEnter={() => setActiveIdx(idx)}
                      onClick={() => runIndex(idx)}
                      className={cn(
                        'w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm text-left',
                        isActive
                          ? 'bg-brand-50 text-brand-800'
                          : 'text-slate-700 hover:bg-slate-50'
                      )}
                    >
                      <span className="flex-1 truncate">{it.label}</span>
                      {isActive && <ArrowRight className="w-3.5 h-3.5" />}
                    </button>
                  );
                })}
              </div>
            ))
          )}
        </div>

        <div className="border-t border-slate-100 px-4 py-2 text-[11px] text-slate-400 flex items-center justify-between">
          <span>
            <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded font-mono">↑↓</kbd>{' '}
            {t('palette.hint.nav')}{' · '}
            <kbd className="px-1.5 py-0.5 bg-slate-100 border border-slate-200 rounded font-mono">↵</kbd>{' '}
            {t('palette.hint.select')}
          </span>
          <span>Command Palette</span>
        </div>
      </div>
    </div>,
    document.body
  );
}
